import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/lib/api'
import { abschnittsfehler } from '@/lib/abschnittsfehler'
import { sprachtext } from '@/lib/sprachtext'
import { uebersetze } from '@/lib/uebersetzen'
import {
  VORLAEUFIG,
  istVorlaeufig,
  vorschauEntwurf,
  wende,
  type Auftrag,
  type VorschauAbschnitt,
} from '@/lib/seitenplan-entwurf'
import { useToast } from '@/composables/useToast'
import { useEditorStore, type EditorContext } from '@/stores/editor'
import { useUmbauVerlaufStore } from '@/stores/umbauVerlauf'
import type {
  Baustein,
  BausteinFeld,
  BausteinKatalog,
  JsonSchemaProperty,
  PlanAbschnitt,
  Seitenplan,
  SeitenplanAktion,
} from '@/types/api'

/**
 * Baukasten-Seiten (Welle 7): Seitenplan und Bausteinkatalog der offenen Seite.
 *
 * Der Plan sagt nur, welche Abschnitte in welcher Reihenfolge stehen. Die
 * Texte stehen weiter in der Übersetzung der Seite, unter Schlüsseln wie
 * `k7f2:titel` (Server: Domain/Seitenplan/Abschnittsschluessel.php). Dieser
 * Speicher übersetzt zwischen beidem: Er sagt der Feldliste, zu welchem
 * Abschnitt ein Text gehört und wie das Feld heißt, und er führt die
 * Umbau-Aktionen aus.
 *
 * Umbauten erscheinen sofort (27.10.2026): Der gezeigte Plan ist der vom
 * Server bestätigte plus die Aufträge, die noch unterwegs sind. Gerechnet wird
 * in lib/seitenplan-entwurf.ts, mit denselben Regeln wie im Server.
 *
 * Geladen wird mit jeder Feldliste (editor.load), damit Plan und Texte immer
 * vom selben Stand sind.
 */

/** Dieselbe Form wie im Server und im Bau (miracms-seitenplan.ts). */
const SCHLUESSEL = /^([a-z][a-z0-9]{3}):([a-z][a-z0-9_]{0,39})(?::([a-z][a-z0-9]{3}):([a-z][a-z0-9_]{0,39}))?$/

export interface Abschnittsteil {
  abschnitt: string
  feld: string
  liste: string | null
  eintrag: string | null
}

export function zerlege(schluessel: string): Abschnittsteil | null {
  const m = SCHLUESSEL.exec(schluessel)
  if (!m) return null
  return m[3]
    ? { abschnitt: m[1]!, feld: m[4]!, liste: m[2]!, eintrag: m[3] }
    : { abschnitt: m[1]!, feld: m[2]!, liste: null, eintrag: null }
}

/** Ein Auftrag samt dem Ort, für den er gilt. */
interface OffenerAuftrag extends Auftrag {
  ctx: EditorContext
}

/** Was die Vorschau bekommt, damit sie den Umbau zeigt, bevor er gebaut ist. */
export interface VorschauNachricht {
  seite: string
  abschnitte: (VorschauAbschnitt & { text?: string })[]
  /** Dieser Abschnitt soll ins Bild, etwa nach einem Klick auf „nach unten“. */
  blick: string | null
  wartetText: string
}

export const useSeitenplanStore = defineStore('seitenplan', () => {
  const katalog = ref<BausteinKatalog | null>(null)
  let katalogFuer: string | null = null
  /** Der Plan, wie ihn der Server zuletzt bestätigt hat. */
  const bestaetigt = ref<Seitenplan | null>(null)
  /** Umbauten, die der Server noch nicht bestätigt hat. Der erste läuft gerade. */
  const auftraege = ref<OffenerAuftrag[]>([])
  /** Der Plan, den die Vorschau zeigt: Stand des letzten Baus. */
  const gebaut = ref<Seitenplan | null>(null)
  /** Kopien und ihre Vorlage, bis der Bau die Kopie liefert. */
  const herkunft = ref<Record<string, string>>({})
  /** Für welche Seite der Plan gilt: Projekt, Seite. */
  const planFuer = ref<{ projectId: string; seite: string } | null>(null)
  /** Gehen gerade Aufträge an den Server? */
  const arbeitet = ref(false)
  /** Der Abschnitt des letzten Klicks; die Vorschau holt ihn ins Bild. */
  const blick = ref<string | null>(null)
  /**
   * Was eben geschehen ist, für Vorleseprogramme (WCAG 4.1.3): Ohne Ansage
   * merkt niemand, der die Vorschau nicht sieht, dass der Klick gewirkt hat.
   * Die Nummer zählt mit, damit dieselbe Ansage zweimal hintereinander auch
   * zweimal gesprochen wird.
   */
  const ansage = ref('')
  const ansageNr = ref(0)
  let naechsteNummer = 1
  /** Wartet, bis alle Aufträge beim Server sind. */
  let leer: Promise<void> = Promise.resolve()
  const toast = useToast()

  function ersteVariante(typ: string): string | undefined {
    return katalog.value?.bausteine[typ]?.varianten[0]?.id
  }

  /** Der Plan, wie er gerade zu sehen ist: bestätigt plus unterwegs. */
  const plan = computed<Seitenplan | null>(() => {
    if (bestaetigt.value === null) return null
    return auftraege.value.reduce((p, a) => wende(p, a, ersteVariante), bestaetigt.value)
  })

  const abschnitte = computed<PlanAbschnitt[]>(() => plan.value?.abschnitte ?? [])

  function gehoertZu(c: EditorContext): boolean {
    return planFuer.value?.projectId === c.projectId && planFuer.value?.seite === c.page
  }

  /**
   * Plan und Katalog zur geöffneten Seite holen. Hat die Seite keinen Plan,
   * gibt es auch keinen Reiter „Abschnitte“, und der Speicher leert sich.
   *
   * Laufen noch Aufträge, wird erst ihr Ende abgewartet: Sonst holte der
   * Server-Stand sie ein, bevor sie gespeichert sind, und der Umbau sähe kurz
   * so aus, als wäre er verloren.
   */
  async function laden(c: EditorContext, seitenMitPlan: string[]): Promise<void> {
    await leer
    if (!seitenMitPlan.includes(c.page)) {
      bestaetigt.value = null
      gebaut.value = null
      planFuer.value = null
      herkunft.value = {}
      blick.value = null
      return
    }
    try {
      if (katalogFuer !== c.projectId || katalog.value === null) {
        katalog.value = (await api.bausteine(c.projectId)).katalog
        katalogFuer = c.projectId
      }
      const antwort = await api.seitenplan(c.projectId, c.page)
      // Inzwischen auf eine andere Seite gewechselt: Deren Laden gilt.
      const jetzt = useEditorStore().ctx
      if (jetzt && (jetzt.projectId !== c.projectId || jetzt.page !== c.page)) return
      bestaetigt.value = antwort.plan
      // Beim Öffnen gilt: Die Vorschau zeigt, was der Server hat.
      gebaut.value = antwort.plan
      planFuer.value = { projectId: c.projectId, seite: c.page }
      herkunft.value = {}
      blick.value = null
    } catch (e) {
      bestaetigt.value = null
      gebaut.value = null
      planFuer.value = null
      toast.error(uebersetze('abschnitte.laden_fehlgeschlagen'), abschnittsfehler(e))
    }
  }

  function baustein(typ: string): Baustein | undefined {
    return katalog.value?.bausteine[typ]
  }

  /** Überschrift der Gruppe in der Feldliste: „2. Häufige Fragen“. */
  function gruppenname(index: number, a: PlanAbschnitt): string {
    return `${index + 1}. ${sprachtext(baustein(a.typ)?.name) || a.typ}`
  }

  /** Gruppen der Abschnitte in der Reihenfolge der Seite, für die Feldliste. */
  const gruppenReihenfolge = computed<string[]>(() => abschnitte.value.map((a, i) => gruppenname(i, a)))

  /** Gilt der geladene Plan für diese Seite? */
  function istPlanseite(seite: string): boolean {
    return plan.value !== null && planFuer.value?.seite === seite
  }

  /**
   * Die Regel eines Abschnitts-Textes, so wie die Feldliste und der Editor sie
   * von einem Schema-Feld kennen: Gruppe = Abschnitt, Beschriftung in der
   * Sprache des Panels, bei Listen mit Nummer („Frage 2“).
   */
  function propFuer(schluessel: string, seite: string): JsonSchemaProperty | undefined {
    if (!istPlanseite(seite)) return undefined
    const t = zerlege(schluessel)
    if (!t) return undefined
    const index = abschnitte.value.findIndex((a) => a.id === t.abschnitt)
    const a = abschnitte.value[index]
    const b = a ? baustein(a.typ) : undefined
    if (!a || !b) return undefined
    let feld: BausteinFeld | undefined
    let nummer = ''
    if (t.liste === null) {
      feld = b.felder[t.feld]
    } else {
      feld = b.listen[t.liste]?.felder[t.feld]
      const stelle = (a.listen?.[t.liste] ?? []).indexOf(t.eintrag ?? '')
      if (stelle < 0) return undefined
      nummer = ` ${stelle + 1}`
    }
    if (!feld) return undefined
    const ui = feld['x-cms-ui'] ?? {}
    // `alt` nennt im Baustein ein Feld desselben Abschnitts (`bild_alt`), hier
    // braucht es den vollen Schlüssel: Daran hängt „bitte prüfen“ nach einem
    // Bildtausch. Dieselbe Regel im Server: AbschnittsEigenschaften.php.
    let altSchluessel: string | undefined
    if (ui.alt) {
      altSchluessel = t.liste !== null && b.listen[t.liste]?.felder[ui.alt]
        ? `${a.id}:${t.liste}:${t.eintrag}:${ui.alt}`
        : `${a.id}:${ui.alt}`
    }
    return {
      ...feld,
      type: feld.type ?? 'string',
      'x-cms-ui': {
        ...ui,
        label: sprachtext(ui.label) + nummer,
        group: gruppenname(index, a),
        widget: ui.widget ?? 'string',
        ...(altSchluessel ? { alt: altSchluessel } : {}),
      },
    }
  }

  /**
   * Wo ein Abschnitts-Text in seinem Abschnitt steht: erst die Felder in der
   * Reihenfolge des Bausteins, dann die Einträge der Listen in ihrer
   * Reihenfolge. Die Datei kennt diese Ordnung nicht, neue Texte stehen dort
   * hinten.
   */
  function rang(schluessel: string): number {
    const t = zerlege(schluessel)
    const a = t ? abschnitte.value.find((x) => x.id === t.abschnitt) : undefined
    const b = a ? baustein(a.typ) : undefined
    if (!t || !a || !b) return Number.MAX_SAFE_INTEGER
    if (t.liste === null) return Object.keys(b.felder).indexOf(t.feld)
    const listen = Object.keys(b.listen)
    const felder = Object.keys(b.listen[t.liste]?.felder ?? {})
    const stelle = (a.listen?.[t.liste] ?? []).indexOf(t.eintrag ?? '')
    return 1000 * (listen.indexOf(t.liste) + 1) + 50 * stelle + felder.indexOf(t.feld)
  }

  /** Der erste Text eines Abschnitts, meist seine Überschrift. */
  function ersterSchluessel(a: PlanAbschnitt): string | null {
    if (istVorlaeufig(a.id)) return null
    const feld = Object.keys(baustein(a.typ)?.felder ?? {})[0]
    return feld === undefined ? null : `${a.id}:${feld}`
  }

  /**
   * Einen Abschnitt wählen, etwa nach einem Klick in der Vorschau neben jeden
   * Text: Sein erster Text öffnet sich, die Abschnitts-Leiste markiert ihn.
   */
  function fokussiere(id: string, seite: string): void {
    if (!istPlanseite(seite)) return
    const a = abschnitte.value.find((x) => x.id === id)
    const schluessel = a ? ersterSchluessel(a) : null
    if (schluessel !== null) useEditorStore().focusField(schluessel, seite)
  }

  /** Was die Vorschau zeigen soll, bis der Bau nachkommt. */
  const vorschau = computed<VorschauNachricht | null>(() => {
    const p = plan.value
    const g = gebaut.value
    const seite = planFuer.value?.seite
    if (p === null || g === null || seite === undefined) return null
    return {
      seite,
      abschnitte: vorschauEntwurf(p, g, herkunft.value).map((v) => {
        if (!v.neu) return v
        const a = p.abschnitte.find((x) => x.id === v.id)
        const name = sprachtext(a ? baustein(a.typ)?.name : undefined) || a?.typ || ''
        return { ...v, text: uebersetze('abschnitte.vorschau_neu', { name }) }
      }),
      blick: blick.value,
      wartetText: uebersetze('abschnitte.vorschau_wartet'),
    }
  })

  /**
   * Den Aufbau ändern.
   *
   * Der Umbau ist sofort zu sehen; der Auftrag geht in die Reihe und von dort
   * an den Server, der prüft und Plan und Texte in einem Commit speichert.
   * Gibt `false` zurück, wenn der Auftrag gar nicht erst angenommen wurde.
   *
   * Ungespeicherte Texte stehen dem nicht mehr im Weg: Die Feldliste lädt nach
   * dem Umbau nur nach, was sich geändert hat, und behält, was getippt ist
   * (editor.aktualisiereWerte).
   */
  async function aktion(a: SeitenplanAktion): Promise<boolean> {
    const c = useEditorStore().ctx
    if (!c || !istPlanseite(c.page)) return false
    // Was es erst vorläufig gibt, kennt der Server noch nicht.
    if ('abschnitt' in a && istVorlaeufig(a.abschnitt)) return false
    if ('eintrag' in a && istVorlaeufig(a.eintrag)) return false
    const neu = a.aktion === 'anlegen' || a.aktion === 'duplizieren' || a.aktion === 'eintrag_anlegen'
      ? `${VORLAEUFIG}${naechsteNummer++}`
      : null
    if (a.aktion === 'duplizieren' && neu !== null) {
      herkunft.value = { ...herkunft.value, [neu]: a.abschnitt }
    }
    auftraege.value = [...auftraege.value, { aktion: a, neu, ctx: { ...c } }]
    if (a.aktion !== 'sichtbar' && a.aktion !== 'entwurf') {
      ansage.value = uebersetze(`abschnitte.erledigt.${a.aktion}`)
      ansageNr.value++
    }
    blick.value = a.aktion === 'anlegen' || a.aktion === 'duplizieren'
      ? neu
      : 'abschnitt' in a ? a.abschnitt : null
    if (!arbeitet.value) leer = abarbeiten()
    return true
  }

  /**
   * Die Aufträge der Reihe nach an den Server geben.
   *
   * Lehnt er einen ab, gilt wieder sein Stand, und die übrigen fallen weg:
   * Sie waren auf einem Aufbau ausgerechnet, den es so nicht gibt.
   * Kehrt zurück, sobald die Reihe leer ist. Texte nachladen und auf den Bau
   * warten läuft danach für sich (nachDemUmbau), damit ein Seitenwechsel
   * nicht auf den Bau warten muss.
   */
  async function abarbeiten(): Promise<void> {
    arbeitet.value = true
    const editor = useEditorStore()
    let ort: EditorContext | null = null
    let gespeichert = 0
    /** Sekunden, als der letzte Auftrag abging: Der Bau danach zählt. */
    let seit = 0
    let fokus: string | null = null
    try {
      while (auftraege.value.length > 0) {
        const auftrag = auftraege.value[0]!
        ort = auftrag.ctx
        seit = Math.floor(Date.now() / 1000)
        const vorher = bestaetigt.value
        try {
          const antwort = await api.seitenplanAktion(ort.projectId, ort.page, ort.site, auftrag.aktion)
          gespeichert++
          if (gehoertZu(ort)) {
            bestaetigt.value = antwort.plan
            fokus = neuerFokus(auftrag, antwort.abschnitt, vorher, antwort.plan) ?? fokus
            uebernimmKennung(auftrag, antwort.abschnitt)
          }
          auftraege.value = auftraege.value.slice(1)
          editor.lastSavedAt = Date.now()
          useUmbauVerlaufStore().merke(ort.projectId, antwort.git.committed ? antwort.git.hash : null, auftrag.aktion.aktion)
          if (!antwort.git.committed || !antwort.git.pushed) {
            toast.error(
              uebersetze('meldung.gespeichert_ungesichert'),
              antwort.git.error?.trim() || uebersetze('meldung.nicht_bei_github'),
            )
          }
        } catch (e) {
          toast.error(uebersetze('meldung.ging_nicht'), abschnittsfehler(e))
          auftraege.value = []
          herkunft.value = Object.fromEntries(Object.entries(herkunft.value).filter(([k]) => !istVorlaeufig(k)))
          if (blick.value !== null && istVorlaeufig(blick.value)) blick.value = null
          // Der Server weiß, was gilt. Neu holen statt raten.
          try {
            const stand = (await api.seitenplan(ort.projectId, ort.page)).plan
            if (gehoertZu(ort)) bestaetigt.value = stand
          } catch {
            // dann eben beim nächsten Laden
          }
          break
        }
      }
    } finally {
      arbeitet.value = false
    }
    if (ort !== null && gespeichert > 0) void nachDemUmbau(ort, seit, fokus)
  }

  /**
   * Texte nachladen (neue Abschnitte bringen welche mit, gelöschte nehmen ihre
   * mit), dann auf den Bau warten. Der Bau nach dem letzten Auftrag zeigt alles
   * bis hierher; kam inzwischen ein neuer dazu, bleibt der als Entwurf über
   * der Vorschau stehen, bis auch sein Bau da ist.
   */
  async function nachDemUmbau(ort: EditorContext, seit: number, fokus: string | null): Promise<void> {
    const editor = useEditorStore()
    await editor.aktualisiereWerte(ort)
    if (fokus !== null && gehoertZu(ort)) editor.focusField(fokus, ort.page)
    const stand = bestaetigt.value
    await editor.wartAufVorschau(seit)
    if (gehoertZu(ort) && stand !== null) gebaut.value = stand
  }

  /** Nach dem Anlegen: Die vorläufige Kennung heißt jetzt so wie beim Server. */
  function uebernimmKennung(auftrag: Auftrag, echt: string | null): void {
    if (auftrag.neu === null || echt === null || auftrag.aktion.aktion === 'eintrag_anlegen') return
    const quelle = herkunft.value[auftrag.neu]
    const rest = Object.fromEntries(Object.entries(herkunft.value).filter(([k]) => k !== auftrag.neu))
    herkunft.value = quelle === undefined ? rest : { ...rest, [echt]: quelle }
    if (blick.value === auftrag.neu) blick.value = echt
  }

  /**
   * Welcher Text sich nach dem Auftrag öffnet: beim neuen Abschnitt und bei
   * der Kopie die Überschrift, beim neuen Eintrag sein erstes Feld.
   */
  function neuerFokus(auftrag: Auftrag, echt: string | null, vorher: Seitenplan | null, nachher: Seitenplan): string | null {
    const a = auftrag.aktion
    if ((a.aktion === 'anlegen' || a.aktion === 'duplizieren') && echt !== null) {
      const neu = nachher.abschnitte.find((x) => x.id === echt)
      return neu ? ersterSchluessel(neu) : null
    }
    if (a.aktion === 'eintrag_anlegen') {
      const alt = vorher?.abschnitte.find((x) => x.id === a.abschnitt)?.listen?.[a.liste] ?? []
      const abschnitt = nachher.abschnitte.find((x) => x.id === a.abschnitt)
      const eintrag = (abschnitt?.listen?.[a.liste] ?? []).find((e) => !alt.includes(e))
      const feld = abschnitt ? Object.keys(baustein(abschnitt.typ)?.listen[a.liste]?.felder ?? {})[0] : undefined
      return eintrag !== undefined && feld !== undefined ? `${a.abschnitt}:${a.liste}:${eintrag}:${feld}` : null
    }
    return null
  }

  function $reset(): void {
    katalog.value = null
    katalogFuer = null
    bestaetigt.value = null
    gebaut.value = null
    planFuer.value = null
    herkunft.value = {}
    blick.value = null
  }

  return {
    katalog,
    plan,
    abschnitte,
    arbeitet,
    vorschau,
    ansage,
    ansageNr,
    laden,
    baustein,
    gruppenname,
    gruppenReihenfolge,
    istPlanseite,
    propFuer,
    rang,
    ersterSchluessel,
    fokussiere,
    aktion,
    $reset,
  }
})
